import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash, FileText, Eye, Pencil, ShareNetwork, WhatsappLogo, Copy, FilePdf, Upload, X, CookingPot, MagnifyingGlass, Camera, FileXls, Gear, Scales, Buildings, ArrowsClockwise, SortAscending } from "@phosphor-icons/react";
import { toast } from "sonner";
import RecipeImportAI from "@/components/RecipeImportAI";
import ExportTemplateConfig from "@/components/ExportTemplateConfig";
import ExportSelectorDialog from "@/components/ExportSelectorDialog";
import { resolveImageUrl, formatCurrency, calcBakerPercent, calcGramaturaTotalFromList, NON_METRIC_UNITS } from "@/pages/sheetUtils";
import { EstimatedPriceBadge } from "@/components/IngredientCombobox";

export default function ExportImportModals(props) {
  const { fetchData, handleEdit, handleExportPDF, handleShare, handleShareWhatsApp, importAIOpen, ingredients, setImportAIOpen, setShowExportSelector, setShowMeasures, setShowTemplateConfig, setViewDialogOpen, showExportSelector, showMeasures, showTemplateConfig, t, viewDialogOpen, viewingSheet } = props;
  return (
    <>
          <ExportTemplateConfig open={showTemplateConfig} onClose={() => setShowTemplateConfig(false)} />
          <ExportSelectorDialog open={showExportSelector} onClose={() => setShowExportSelector(false)} type="ficha_tecnica" />
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold tracking-tight">
              Ficha Técnica - {viewingSheet?.name}
            </DialogTitle>
            <DialogDescription className="sr-only">Detalhes da ficha técnica</DialogDescription>
          </DialogHeader>
          
          {viewingSheet && (
            <div className="space-y-6 mt-4">
              {/* Etapa 11: Rotulagem ANVISA — alergênicos e dietas */}
              {viewingSheet.allergens?.allergens_badges?.length > 0 && (
                <div className="space-y-2" data-testid="allergen-badges">
                  <div className="flex flex-wrap gap-1.5">
                    {viewingSheet.allergens.allergens_badges.map((badge) => (
                      <span
                        key={badge}
                        data-testid={`allergen-badge-${badge}`}
                        className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${
                          badge === "vegano" || badge === "vegetariano"
                            ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                            : "bg-rose-50 text-rose-700 border-rose-200"
                        }`}
                      >
                        {badge === "vegano" ? "🌱 Vegano" : badge === "vegetariano" ? "🥬 Vegetariano" : `Contém ${badge}`}
                      </span>
                    ))}
                  </div>
                  {viewingSheet.allergens.cross_contamination_alert && (
                    <p className="text-xs font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5" data-testid="cross-contamination-alert">
                      ⚠️ Alerta ANVISA: risco de contaminação cruzada detectado nos insumos desta receita.
                    </p>
                  )}
                </div>
              )}
              {/* Foto do Produto */}
              {viewingSheet.foto_url && (
                <div>
                  <img 
                    src={resolveImageUrl(viewingSheet.foto_url)} 
                    alt={viewingSheet.name} 
                    className="w-full h-64 object-cover rounded-sm border border-zinc-200"
                  />
                </div>
              )}
              
              {/* Informações Gerais */}
              <div className="grid grid-cols-2 gap-3 md:gap-4 p-3 md:p-4 bg-zinc-50 border border-zinc-200 rounded-sm">
                <div>
                  <p className="text-[10px] md:text-xs uppercase tracking-wider text-zinc-500 font-semibold">Rendimento</p>
                  <p className="text-base md:text-lg font-mono font-bold text-zinc-900 mt-1">
                    {viewingSheet.yield_quantity} {viewingSheet.yield_unit}
                  </p>
                </div>
                {viewingSheet.gramatura_porcao && (
                  <div>
                    <p className="text-[10px] md:text-xs uppercase tracking-wider text-zinc-500 font-semibold">Gramatura/Porção</p>
                    <p className="text-base md:text-lg font-mono font-bold text-zinc-900 mt-1">
                      {viewingSheet.gramatura_porcao} kg
                    </p>
                  </div>
                )}
                <div>
                  <p className="text-[10px] md:text-xs uppercase tracking-wider text-zinc-500 font-semibold">Qtd. Total</p>
                  <p className="text-base md:text-lg font-mono font-bold text-zinc-900 mt-1">
                    {calcGramaturaTotalFromList(viewingSheet.ingredients).toFixed(3)} kg
                  </p>
                </div>
                <div>
                  <p className="text-[10px] md:text-xs uppercase tracking-wider text-zinc-500 font-semibold">Custo Total</p>
                  <p className="text-base md:text-lg font-mono font-bold text-zinc-900 mt-1">
                    {formatCurrency(viewingSheet.total_cost)}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] md:text-xs uppercase tracking-wider text-zinc-500 font-semibold">Custo/Unidade</p>
                  <p className="text-base md:text-lg font-mono font-bold text-green-600 mt-1">
                    {formatCurrency(viewingSheet.cost_per_unit)}
                  </p>
                </div>
              </div>
              
              {/* Modo de Preparo */}
              {viewingSheet.modo_preparo && (
                <div className="p-4 bg-zinc-50 border border-zinc-200 rounded-sm">
                  <h3 className="text-sm font-semibold text-zinc-900 mb-2">Modo de Preparo</h3>
                  <p className="text-sm text-zinc-700 whitespace-pre-wrap">{viewingSheet.modo_preparo}</p>
                </div>
              )}
              
              {/* Lista de Ingredientes */}
              <div>
                <h3 className="text-sm font-semibold text-zinc-900 mb-3">
                  Ingredientes
                  {viewingSheet.formula_padeiro && <span className="ml-2 text-xs font-normal text-amber-700 bg-amber-50 px-2 py-0.5 rounded">Fórmula do Padeiro</span>}
                </h3>
                <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-zinc-50">
                      <TableHead className="text-xs uppercase tracking-wider font-semibold text-zinc-500">Ingrediente</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider font-semibold text-zinc-500 text-center">Qtd. Bruta</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider font-semibold text-zinc-500 text-center">FC</TableHead>
                      <TableHead className="text-xs uppercase tracking-wider font-semibold text-zinc-500 text-center">Qtd. Líquida</TableHead>
                      {viewingSheet.formula_padeiro && viewingSheet.ingrediente_base_id && (
                        <TableHead className="text-xs uppercase tracking-wider font-semibold text-amber-700 text-center">%</TableHead>
                      )}
                      <TableHead className="text-xs uppercase tracking-wider font-semibold text-zinc-500 text-right number">Custo</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {viewingSheet.ingredients.map((ing, index) => (
                      <TableRow key={ing.ingredient_id || index} className="table-row">
                        <TableCell className="font-medium text-zinc-900">
                          {ing.ingredient_name}
                          {ing.is_qb && <span className="ml-2 text-[10px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">QB</span>}
                          {ing.is_sub_recipe && <span className="ml-2 text-[10px] font-bold text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded">SUB</span>}
                          {!ing.is_sub_recipe && ingredients.find(c => c.id === ing.ingredient_id)?.is_estimated && <EstimatedPriceBadge className="ml-2" testId={`view-est-badge-${index}`} />}
                          {ing.peso_equivalente_g && !ing.is_sub_recipe && <span className="ml-1 text-[10px] text-blue-600">(~{ing.peso_equivalente_g}g/{ing.unit})</span>}
                        </TableCell>
                        <TableCell className="text-center text-zinc-700">
                          {ing.is_qb ? <span className="text-amber-700 font-semibold text-xs">QB</span> : <><span className="font-mono">{ing.quantidade_bruta}</span> {ing.unit}</>}
                        </TableCell>
                        <TableCell className="text-center text-zinc-700 font-mono">
                          {ing.is_qb ? "-" : ing.fator_coccao.toFixed(2)}
                        </TableCell>
                        <TableCell className="text-center text-zinc-700">
                          {ing.is_qb ? <span className="text-amber-700 font-semibold text-xs">QB</span> : <><span className="font-mono">{ing.quantidade_liquida}</span> {ing.unit}</>}
                        </TableCell>
                        {viewingSheet.formula_padeiro && viewingSheet.ingrediente_base_id && (
                          <TableCell className="text-center text-amber-700 font-mono font-semibold">
                            {ing.is_qb ? "-" : (
                              ing.ingredient_id === viewingSheet.ingrediente_base_id
                                ? "100%"
                                : `${calcBakerPercent(ing, viewingSheet.ingredients, viewingSheet.ingrediente_base_id) || '-'}%`
                            )}
                          </TableCell>
                        )}
                        <TableCell className="text-right font-mono number text-zinc-900">
                          {ing.is_qb ? "-" : formatCurrency(ing.item_cost)}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="bg-zinc-50 font-semibold">
                      <TableCell colSpan={viewingSheet.formula_padeiro && viewingSheet.ingrediente_base_id ? 5 : 4} className="text-zinc-900">Total</TableCell>
                      <TableCell className="text-right font-mono number text-zinc-900">
                        {formatCurrency(viewingSheet.total_cost)}
                      </TableCell>
                    </TableRow>
                    <TableRow className="bg-blue-50 font-semibold">
                      <TableCell colSpan={viewingSheet.formula_padeiro && viewingSheet.ingrediente_base_id ? 5 : 4} className="text-blue-900">Quantidade aproximada total</TableCell>
                      <TableCell className="text-right font-mono number text-blue-900">
                        {calcGramaturaTotalFromList(viewingSheet.ingredients).toFixed(3)} kg
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
                </div>
              </div>
              
              {/* Botões de Ação */}
              <div className="flex flex-wrap justify-end gap-2 pt-4 border-t border-zinc-200">
                <Button
                  variant="outline"
                  onClick={() => handleExportPDF(viewingSheet)}
                  data-testid="pdf-from-view-btn"
                  className="border-red-600 text-red-600 hover:bg-red-50"
                >
                  <FilePdf size={18} className="mr-2" weight="fill" />
                  Exportar PDF
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleShareWhatsApp(viewingSheet)}
                  data-testid="whatsapp-from-view-btn"
                  className="border-green-600 text-green-600 hover:bg-green-50"
                >
                  <WhatsappLogo size={18} className="mr-2" weight="fill" />
                  WhatsApp
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleShare(viewingSheet)}
                  data-testid="share-from-view-btn"
                >
                  <Copy size={18} className="mr-2" />
                  Copiar
                </Button>
                <Button
                  onClick={() => {
                    setViewDialogOpen(false);
                    handleEdit(viewingSheet);
                  }}
                  className="bg-zinc-950 text-white hover:bg-zinc-800"
                  data-testid="edit-from-view-btn"
                >
                  <Pencil size={18} className="mr-2" />
                  Editar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <RecipeImportAI
        open={importAIOpen}
        onClose={() => setImportAIOpen(false)}
        ingredients={ingredients}
        onImported={fetchData}
      />

      {/* Measures Help Dialog */}
      <Dialog open={showMeasures} onOpenChange={setShowMeasures}>
        <DialogContent className="sm:max-w-[600px] max-h-[85vh] overflow-y-auto" data-testid="measures-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg font-semibold">
              <Scales size={20} />
              Conversão de Medidas
            </DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">
              Referência rápida para conversão de medidas culinárias
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="border rounded-lg p-3">
                <p className="text-xs font-semibold text-zinc-700 mb-2">Referência Rápida</p>
                <div className="space-y-1 text-xs text-zinc-600">
                  <p>1 colher de chá = <span className="font-mono font-semibold">5 ml</span></p>
                  <p>1 colher de sopa = <span className="font-mono font-semibold">15 ml</span></p>
                  <p>1 xícara (chá) = <span className="font-mono font-semibold">240 ml</span></p>
                  <p>1 copo americano = <span className="font-mono font-semibold">200 ml</span></p>
                  <p>1 litro = <span className="font-mono font-semibold">1000 ml</span></p>
                </div>
              </div>
              <div className="border rounded-lg p-3">
                <p className="text-xs font-semibold text-zinc-700 mb-2">Peso / Volume</p>
                <div className="space-y-1 text-xs text-zinc-600">
                  <p>1 kg = <span className="font-mono font-semibold">1000 g</span></p>
                  <p>1 g = <span className="font-mono font-semibold">1000 mg</span></p>
                  <p>1 L = <span className="font-mono font-semibold">1000 ml</span></p>
                  <p>1 oz = <span className="font-mono font-semibold">28.35 g</span></p>
                  <p>1 lb = <span className="font-mono font-semibold">453.6 g</span></p>
                </div>
              </div>
            </div>
            <div className="border rounded-lg overflow-hidden">
              <p className="text-xs font-semibold text-zinc-700 px-3 py-2 bg-zinc-50 border-b">Conversão por Ingrediente (1 colher de sopa)</p>
              <div className="divide-y text-xs">
                {[
                  ["Água / Leite", "15 g"],
                  ["Óleo / Azeite", "12 g"],
                  ["Açúcar", "12 g"],
                  ["Farinha de trigo", "7.5 g"],
                  ["Sal fino", "15 g"],
                  ["Fermento em pó", "10 g"],
                  ["Manteiga derretida", "12 g"],
                  ["Cacau em pó", "6 g"],
                  ["Mel", "21 g"],
                  ["Amido de milho", "8 g"],
                ].map(([ing, g]) => (
                  <div key={ing} className="flex justify-between px-3 py-1.5 hover:bg-zinc-50">
                    <span className="text-zinc-700">{ing}</span>
                    <span className="font-mono font-semibold text-zinc-900">{g}</span>
                  </div>
                ))}
              </div>
            </div>
            <p className="text-[10px] text-zinc-400 text-center">Valores aproximados. Pode variar conforme o ingrediente.</p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
